import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, Table, Alert, Spinner, Card } from 'react-bootstrap';
import axios from 'axios';
import DriverSelector from '../../components/driverSelector';

// Валідація PESEL (11 цифр, контрольна сума)
function validatePesel(pesel) {
  const s = String(pesel || '').replace(/\s/g, '');
  if (!/^\d{11}$/.test(s)) return { valid: false, message: 'PESEL має містити рівно 11 цифр' };
  const weights = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
  let sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(s[i], 10) * weights[i];
  const check = (10 - (sum % 10)) % 10;
  if (check !== parseInt(s[10], 10)) return { valid: false, message: 'Невірна контрольна сума PESEL' };
  return { valid: true };
}

// Валідація номера рахунку (Польща: рівно 26 цифр, без PL)
function validateBankAccount(account) {
  const s = String(account || '').replace(/\s/g, '');
  if (!s) return { valid: false, message: 'Вкажіть номер рахунку' };
  if (/^\d{26}$/.test(s)) return { valid: true };
  return { valid: false, message: 'Номер рахунку: рівно 26 цифр (без PL і пробілів)' };
}

const defaultSeller = {
  name: '',
  address: '',
  city: '',
  pesel: ''
};

const defaultBuyer = {
  name: 'TORI Viktoriya Manachynska',
  address: 'ul. Księcia Witolda 49/15',
  city: '50-202 Wrocław',
  nip: '7822599564'
};

const InvoiceConstructor = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [validationErrors, setValidationErrors] = useState({ pesel: null, bankAccount: null });
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [savedInvoiceId, setSavedInvoiceId] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [invoiceId, setInvoiceId] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [paymentMethod, setPaymentMethod] = useState('transfer');
  const [paymentDueDate, setPaymentDueDate] = useState(new Date().toISOString().split('T')[0]);
  const [bankAccount, setBankAccount] = useState('');
  const [seller, setSeller] = useState({ ...defaultSeller });
  const [buyer, setBuyer] = useState({ ...defaultBuyer });
  const [rows, setRows] = useState([{ week: '', amount: 0, serviceDescription: '' }]);
  const [description, setDescription] = useState('Wynajem samochodu');
  const [footerText, setFooterText] = useState('');
  const [lastNumber, setLastNumber] = useState(null);

  const months = [
    { value: '01', label: 'Січень' }, { value: '02', label: 'Лютий' }, { value: '03', label: 'Березень' },
    { value: '04', label: 'Квітень' }, { value: '05', label: 'Травень' }, { value: '06', label: 'Червень' },
    { value: '07', label: 'Липень' }, { value: '08', label: 'Серпень' }, { value: '09', label: 'Вересень' },
    { value: '10', label: 'Жовтень' }, { value: '11', label: 'Листопад' }, { value: '12', label: 'Грудень' }
  ];
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const idParam = searchParams.get('invoiceId') || searchParams.get('id');
  const monthParam = searchParams.get('month');
  const yearParam = searchParams.get('year');

  useEffect(() => {
    if (monthParam) setSelectedMonth(monthParam);
    if (yearParam) setSelectedYear(parseInt(yearParam, 10) || new Date().getFullYear());
  }, [monthParam, yearParam]);

  useEffect(() => {
    if (idParam) {
      setLoading(true);
      setError(null);
      axios.get(`/api/invoices/${idParam}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
        .then((res) => {
          const inv = res.data?.invoice;
          if (!inv) return;
          setInvoiceId(inv.id);
          setInvoiceNumber(inv.invoice_number || '');
          setSelectedMonth(String(inv.month).padStart(2, '0'));
          setSelectedYear(inv.year || new Date().getFullYear());
          setPaymentMethod(inv.payment_method || 'transfer');
          setPaymentDueDate(inv.payment_due_date ? inv.payment_due_date.split('T')[0] : new Date().toISOString().split('T')[0]);
          setBankAccount(String(inv.bank_account || '').replace(/^PL/gi, '').replace(/\D/g, '').slice(0, 26));
          if (inv.driver_id) {
            axios.get(`/api/users/${inv.driver_id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
              .then((driverRes) => {
                const driver = driverRes.data;
                setSelectedDriver({
                  value: driver.id,
                  label: `${driver.firstName || ''} ${driver.lastName || ''}`.trim()
                });
              })
              .catch(() => setSelectedDriver(null));
          } else {
            setSelectedDriver(null);
          }
          setSeller(typeof inv.seller_data === 'object' && inv.seller_data ? { ...defaultSeller, ...inv.seller_data } : { ...defaultSeller });
          setBuyer(typeof inv.buyer_data === 'object' && inv.buyer_data ? { ...defaultBuyer, ...inv.buyer_data } : { ...defaultBuyer });
          const w = Array.isArray(inv.weeks) ? inv.weeks : (typeof inv.weeks === 'string' ? (() => { try { return JSON.parse(inv.weeks); } catch { return []; } })() : []);
          setRows(w.length > 0 ? w.map((r) => ({
            week: r.week ?? '',
            amount: Number(r.amount) || 0,
            serviceDescription: r.serviceDescription != null ? String(r.serviceDescription) : '',
          })) : [{ week: '', amount: 0, serviceDescription: '' }]);
          const invData = typeof inv.invoice_data === 'object' ? inv.invoice_data : {};
          setDescription(invData.description || 'Wynajem samochodu');
          setFooterText(invData.footerText != null ? String(invData.footerText) : '');
        })
        .catch((err) => setError(err.response?.data?.error || err.message || 'Помилка завантаження'))
        .finally(() => setLoading(false));
    }
  }, [idParam]);

  useEffect(() => {
    if (!idParam && selectedMonth && selectedYear) {
      axios.get(`/api/invoices/last-number/${selectedMonth}/${selectedYear}`)
        .then((res) => setLastNumber(res.data?.lastNumber ?? res.data?.lastInvoiceNumber ?? 0))
        .catch(() => setLastNumber(0));
    }
  }, [idParam, selectedMonth, selectedYear]);

  const totalAmount = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  const addRow = () => setRows((prev) => [...prev, { week: '', amount: 0, serviceDescription: '' }]);
  const removeRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index));
  const updateRow = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: field === 'amount' ? (parseFloat(value) || 0) : value };
      return next;
    });
  };

  const handleDriverChange = async (driverId, option) => {
    setSelectedDriver(option);
    setValidationErrors((ev) => ({ ...ev, pesel: null, bankAccount: null }));

    if (!driverId) {
      setSeller({ ...defaultSeller });
      return;
    }

    try {
      const res = await axios.get(`/api/users/${driverId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const driver = res.data || {};
      setSeller({
        name: `${driver.firstName || ''} ${driver.lastName || ''}`.trim(),
        address: `${driver.street || ''} ${driver.house_number || ''}`.trim(),
        city: `${driver.index || ''}, ${driver.city || ''}`.replace(/^,\s*/, ''),
        pesel: driver.pesel || ''
      });
      if (driver.account_number) {
        setBankAccount(String(driver.account_number).replace(/^PL/gi, '').replace(/\D/g, '').slice(0, 26));
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Помилка завантаження даних водія');
    }
  };

  const handleSave = async () => {
    setValidationErrors({ pesel: null, bankAccount: null });
    if (!selectedMonth || !selectedYear) {
      setError('Оберіть місяць та рік');
      return;
    }
    if (seller.pesel && seller.pesel.trim()) {
      const peselResult = validatePesel(seller.pesel);
      if (!peselResult.valid) {
        setValidationErrors((e) => ({ ...e, pesel: peselResult.message }));
        setError(peselResult.message);
        return;
      }
    }
    if (paymentMethod === 'transfer') {
      const bankResult = validateBankAccount(bankAccount);
      if (!bankResult.valid) {
        setValidationErrors((e) => ({ ...e, bankAccount: bankResult.message }));
        setError(bankResult.message);
        return;
      }
    }
    const weeksData = rows.map((r) => ({
      week: r.week,
      amount: Number(r.amount) || 0,
      type: paymentMethod === 'transfer' ? 'payout' : 'cash',
      serviceDescription: r.serviceDescription != null ? String(r.serviceDescription) : '',
    }));
    setError(null);
    setSuccess(null);
    setSaving(true);
    setInvoicePreview(null);
    setSavedInvoiceId(null);
    try {
      const res = await axios.put('/api/invoices/upsert', {
        id: invoiceId || undefined,
        driver_id: selectedDriver?.value ?? null,
        invoice_number: invoiceNumber || undefined,
        month: selectedMonth,
        year: selectedYear,
        weeks: JSON.stringify(weeksData),
        seller_data: seller,
        buyer_data: buyer,
        invoice_data: JSON.stringify({
          total: Math.round(totalAmount * 100) / 100,
          description,
          footerText,
        }),
        payment_method: paymentMethod,
        payment_due_date: paymentDueDate,
        bank_account: bankAccount || undefined,
        status: 'draft'
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const newId = res.data?.invoiceId;
      setSuccess('Рахунок збережено. Можете переглянути та опублікувати нижче.');
      setSavedInvoiceId(newId);
      setInvoiceId(newId || invoiceId);
      if (newId) {
        const htmlRes = await axios.get(`/api/invoices/by-id/${newId}/html`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (htmlRes.data?.html) {
          const invRes = await axios.get(`/api/invoices/${newId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
          setInvoicePreview({
            html: htmlRes.data.html,
            invoice_number: invRes.data?.invoice?.invoice_number || invoiceNumber || ''
          });
        }
      }
      if (!invoiceId && !newId) {
        setInvoiceNumber('');
        setRows([{ week: '', amount: 0, serviceDescription: '' }]);
        const numRes = await axios.get(`/api/invoices/last-number/${selectedMonth}/${selectedYear}`);
        setLastNumber(numRes.data?.lastNumber ?? numRes.data?.lastInvoiceNumber ?? 0);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Помилка збереження');
    } finally {
      setSaving(false);
    }
  };

  const loadPreview = async () => {
    const id = savedInvoiceId || invoiceId;
    if (!id) return;
    setLoadingPreview(true);
    setError(null);
    try {
      const htmlRes = await axios.get(`/api/invoices/by-id/${id}/html`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (htmlRes.data?.html) {
        const invRes = await axios.get(`/api/invoices/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        setInvoicePreview({
          html: htmlRes.data.html,
          invoice_number: invRes.data?.invoice?.invoice_number || invoiceNumber || ''
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Помилка завантаження перегляду');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSaveAndDownloadPdf = async () => {
    setValidationErrors({ pesel: null, bankAccount: null });
    if (!selectedMonth || !selectedYear) {
      setError('Оберіть місяць та рік');
      return;
    }
    if (seller.pesel && seller.pesel.trim()) {
      const peselResult = validatePesel(seller.pesel);
      if (!peselResult.valid) {
        setValidationErrors((e) => ({ ...e, pesel: peselResult.message }));
        setError(peselResult.message);
        return;
      }
    }
    if (paymentMethod === 'transfer') {
      const bankResult = validateBankAccount(bankAccount);
      if (!bankResult.valid) {
        setValidationErrors((e) => ({ ...e, bankAccount: bankResult.message }));
        setError(bankResult.message);
        return;
      }
    }
    setGeneratingPdf(true);
    setError(null);
    try {
      const weeksData = rows.map((r) => ({
      week: r.week,
      amount: Number(r.amount) || 0,
      type: paymentMethod === 'transfer' ? 'payout' : 'cash',
      serviceDescription: r.serviceDescription != null ? String(r.serviceDescription) : '',
    }));
      const saveRes = await axios.put('/api/invoices/upsert', {
        id: invoiceId || undefined,
        driver_id: selectedDriver?.value ?? null,
        invoice_number: invoiceNumber || undefined,
        month: selectedMonth,
        year: selectedYear,
        weeks: JSON.stringify(weeksData),
        seller_data: seller,
        buyer_data: buyer,
        invoice_data: JSON.stringify({
          total: Math.round(totalAmount * 100) / 100,
          description,
          footerText,
        }),
        payment_method: paymentMethod,
        payment_due_date: paymentDueDate,
        bank_account: bankAccount || undefined,
        status: 'saved'
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const id = saveRes.data?.invoiceId ?? savedInvoiceId ?? invoiceId;
      if (!id) {
        setError('Не вдалося зберегти рахунок');
        return;
      }
      setSavedInvoiceId(id);
      setInvoiceId(id);
      const htmlRes = await axios.get(`/api/invoices/by-id/${id}/html`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const htmlToUse = htmlRes.data?.html;
      if (!htmlToUse) {
        setError('Не вдалося отримати HTML рахунку');
        return;
      }
      const invRes = await axios.get(`/api/invoices/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const invNumber = invRes.data?.invoice?.invoice_number || invoiceNumber || id;
      const pdfRes = await axios.post('/api/invoices/generate-from-html', {
        html: htmlToUse,
        fileName: `Rachunek_${invNumber}.pdf`
      }, { responseType: 'arraybuffer', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rachunek_${invNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setInvoicePreview((prev) => (prev ? { ...prev, html: htmlToUse, invoice_number: invNumber } : { html: htmlToUse, invoice_number: invNumber }));
      setSuccess('Рахунок збережено, опубліковано та PDF завантажено.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Помилка публікації або PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Завантаження...</p>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>📄 Конструктор рахунку (без рапортів)</h2>
        <Button variant="outline-secondary" onClick={() => navigate('/admin/month-summary')}>
          ← Місячна статистика
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      <Row>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Водій</Form.Label>
            <DriverSelector
              selectedDriver={selectedDriver}
              onChange={handleDriverChange}
              activeOnly
              placeholder="Оберіть водія"
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Місяць</Form.Label>
            <Form.Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
              <option value="">Оберіть</option>
              {months.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Рік</Form.Label>
            <Form.Select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Спосіб оплати</Form.Label>
            <Form.Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              <option value="transfer">Переказ</option>
              <option value="cash">Готівка</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Номер рахунку (заповнити для редагування або залишити порожнім для авто)</Form.Label>
            <Form.Control
              type="text"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder={lastNumber != null ? `Наступний: ${lastNumber + 1}` : ''}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Термін оплати</Form.Label>
            <Form.Control type="date" value={paymentDueDate} onChange={(e) => setPaymentDueDate(e.target.value)} />
          </Form.Group>
        </Col>
      </Row>

      {paymentMethod === 'transfer' && (
        <Form.Group className="mb-3">
          <Form.Label>Номер рахунку (банк), без PL <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            inputMode="numeric"
            maxLength={26}
            value={bankAccount}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '');
              setBankAccount(v);
              setValidationErrors((ev) => ({ ...ev, bankAccount: null }));
            }}
            placeholder="26 цифр"
            isInvalid={!!validationErrors.bankAccount}
          />
          <Form.Text className="text-muted">{bankAccount.length}/26 цифр</Form.Text>
          {validationErrors.bankAccount && <Form.Control.Feedback type="invalid">{validationErrors.bankAccount}</Form.Control.Feedback>}
        </Form.Group>
      )}

      <Row className="mt-4">
        <Col md={6}>
          <Card className="h-100 border-primary">
            <Card.Header className="bg-primary text-white py-2">
              <strong>Продавець (Sprzedawca)</strong>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label className="small text-muted">Ім'я та прізвище</Form.Label>
                <Form.Control
                  value={seller.name}
                  onChange={(e) => setSeller((s) => ({ ...s, name: e.target.value }))}
                  placeholder="Jan Kowalski"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small text-muted">PESEL</Form.Label>
                <Form.Control
                  value={seller.pesel}
                  onChange={(e) => { setSeller((s) => ({ ...s, pesel: e.target.value })); setValidationErrors((ev) => ({ ...ev, pesel: null })); }}
                  placeholder="11 цифр"
                  maxLength={11}
                  isInvalid={!!validationErrors.pesel}
                />
                {validationErrors.pesel && <Form.Control.Feedback type="invalid">{validationErrors.pesel}</Form.Control.Feedback>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small text-muted">Вулиця та номер</Form.Label>
                <Form.Control
                  value={seller.address}
                  onChange={(e) => setSeller((s) => ({ ...s, address: e.target.value }))}
                  placeholder="ul. Przykładowa 1/2"
                />
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label className="small text-muted">Місто (індекс, місто)</Form.Label>
                <Form.Control
                  value={seller.city}
                  onChange={(e) => setSeller((s) => ({ ...s, city: e.target.value }))}
                  placeholder="00-000 Warszawa"
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100 border-secondary">
            <Card.Header className="bg-secondary text-white py-2">
              <strong>Покупець (Nabywca)</strong>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label className="small text-muted">Назва / Ім'я</Form.Label>
                <Form.Control
                  value={buyer.name}
                  onChange={(e) => setBuyer((b) => ({ ...b, name: e.target.value }))}
                  placeholder="Nazwa firmy lub imię"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small text-muted">NIP</Form.Label>
                <Form.Control
                  value={buyer.nip}
                  onChange={(e) => setBuyer((b) => ({ ...b, nip: e.target.value }))}
                  placeholder="10 цифр, без дефісів"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small text-muted">Вулиця та номер</Form.Label>
                <Form.Control
                  value={buyer.address}
                  onChange={(e) => setBuyer((b) => ({ ...b, address: e.target.value }))}
                  placeholder="ul. Przykładowa 1"
                />
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label className="small text-muted">Місто (індекс, місто)</Form.Label>
                <Form.Control
                  value={buyer.city}
                  onChange={(e) => setBuyer((b) => ({ ...b, city: e.target.value }))}
                  placeholder="50-202 Wrocław"
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Form.Group className="mb-3 mt-3">
        <Form.Label>Опис послуги за замовчуванням</Form.Label>
        <Form.Text className="d-block text-muted mb-1">Використовується в PDF для позицій, у яких не заповнено власний опис.</Form.Text>
        <Form.Control value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Wynajem samochodu" />
      </Form.Group>

      <h5 className="mt-4">Позиції</h5>
      <Table bordered size="sm" className="align-middle">
        <thead>
          <tr>
            <th style={{ width: '10%' }}>Тиждень / позначка</th>
            <th>Опис послуги (рядок)</th>
            <th style={{ width: '12%' }}>Сума (zł)</th>
            <th style={{ width: '44px' }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              <td><Form.Control size="sm" value={row.week} onChange={(e) => updateRow(idx, 'week', e.target.value)} placeholder="напр. 21" /></td>
              <td>
                <Form.Control
                  size="sm"
                  as="textarea"
                  rows={2}
                  value={row.serviceDescription || ''}
                  onChange={(e) => updateRow(idx, 'serviceDescription', e.target.value)}
                  placeholder="За потреби; якщо порожньо — підставиться опис за замовчуванням"
                />
              </td>
              <td><Form.Control size="sm" type="number" step="0.01" value={row.amount || ''} onChange={(e) => updateRow(idx, 'amount', e.target.value)} /></td>
              <td><Button variant="outline-danger" size="sm" onClick={() => removeRow(idx)}>🗑️</Button></td>
            </tr>
          ))}
          <tr className="table-light">
            <td colSpan={2}><strong>Всього</strong></td>
            <td><strong>{totalAmount.toFixed(2)} zł</strong></td>
            <td><Button variant="outline-success" size="sm" onClick={addRow}>➕ Додати</Button></td>
          </tr>
        </tbody>
      </Table>

      <Form.Group className="mb-3">
        <Form.Label>Текст у кінці рахунку</Form.Label>
        <Form.Text className="d-block text-muted mb-1">Довільний текст після блоку оплати (наприклад примітки, реквізити). Підтримуються переноси рядків.</Form.Text>
        <Form.Control as="textarea" rows={4} value={footerText} onChange={(e) => setFooterText(e.target.value)} placeholder="Необов’язково" />
      </Form.Group>

      <div className="mt-4 mb-4">
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <><Spinner animation="border" size="sm" className="me-2" />Збереження...</> : '💾 Зберегти рахунок'}
        </Button>
        <Button variant="success" className="ms-2" onClick={handleSaveAndDownloadPdf} disabled={generatingPdf || !selectedMonth || !selectedYear}>
          {generatingPdf ? <><Spinner animation="border" size="sm" className="me-2" />Генерація PDF...</> : '💾 Зберегти та завантажити PDF'}
        </Button>
      </div>

      {((savedInvoiceId || invoiceId) && !invoicePreview?.html) && (
        <div className="mt-4 p-3 border rounded bg-light">
          <h5>Перегляд та публікація рахунку</h5>
          <p className="text-muted small">Завантажте перегляд рахунку, потім опублікуйте та завантажте PDF.</p>
          <Button variant="outline-primary" onClick={loadPreview} disabled={loadingPreview}>
            {loadingPreview ? <><Spinner animation="border" size="sm" className="me-2" />Завантаження...</> : '👁 Переглянути рахунок'}
          </Button>
        </div>
      )}

      {invoicePreview?.html && (savedInvoiceId || invoiceId) && (
        <div className="mt-4 p-3 border rounded bg-light">
          <h5>Перегляд та публікація рахунку</h5>
          <p className="text-muted small">Опублікуйте рахунок та завантажте PDF — як для рахунків, створених з рапортів.</p>
          <div className="mb-3">
            <Button variant="success" onClick={handleSaveAndDownloadPdf} disabled={generatingPdf}>
              {generatingPdf ? <><Spinner animation="border" size="sm" className="me-2" />Генерація PDF...</> : '💾 Зберегти як опублікований і завантажити PDF'}
            </Button>
          </div>
          <div className="border bg-white p-2" style={{ maxHeight: '70vh', overflow: 'auto' }}>
            <iframe title="Перегляд рахунку" srcDoc={invoicePreview.html} style={{ width: '100%', minHeight: '800px', border: 'none' }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceConstructor;
